import { List, Map } from 'immutable';

import { isCombinationDeleted, isCombinationForbidden, isOptionDeleted, isVariantDeleted } from './combinations';
import { shapeFaireDistanceUnitForApi, shapeFaireMassUnitForApi } from '../product/shapeAttributes';
import { getOptionFormatter } from '../taxonomy';
import { getDateKey } from '../time';
import { isTruthy } from '../bool';
import { getSize } from '../iterable/getSize';

import { CHANNEL_VARIANTS, COMBINATIONS, DELETED, INFLUENCE, RETAIN, VARIANTS } from '../../constants/variations';
import { AMOUNT, ATTRIBUTE_DEFAULTS, CURRENCY, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { EMPTY, SEPARATOR } from '../../constants';
import { ATTRIBUTE } from '../../constants/attributes';
import { FORM } from '../../constants/forms';

function shapeEbayVariationsForAPI({ product, toImmutable }) {
  const payload = {};
  const retain = product.getIn([FORM.VARIATIONS, RETAIN]);
  const influence = product.getIn([FORM.VARIATIONS, INFLUENCE]);

  function reducePhotos(photos, photo) {
    const url = photo.get('fullsize_url');

    if (toImmutable) {
      return photos.push(Map({ fullsize_url: url }));
    }

    photos.push({ fullsize_url: url });
    return photos;
  }

  function reduceVariants(result, variant) {
    function reduceOptions(values, item) {
      const value = item.get('value');
      const option = { value };

      if (!retain && item.get('photos')) {
        option.photos = item.get('photos').reduce(reducePhotos, toImmutable ? EMPTY.LIST : []);
      }

      if (toImmutable) {
        return values.push(Map(option));
      }

      values.push(option);
      return values;
    }

    const option = {
      name: variant.get('name'),
      value: variant.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []),
    };

    if (toImmutable) {
      return result.push(Map(option));
    }

    result.push(option);

    return result;
  }

  function reduceCombinations(result, combination) {
    if (isCombinationForbidden({ combination, influence })) return result;

    function reduceOptions(options, item, index) {
      const value = item.get('name');
      const name = product.getIn([FORM.VARIATIONS, VARIANTS, index, 'name']);
      const option = { name, value };

      if (toImmutable) {
        return options.push(Map(option));
      }

      options.push(option);
      return options;
    }

    const variation = {
      options: combination.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []),
      price: retain?.get(ATTRIBUTE.PRICE)
        ? null
        : combination.get(ATTRIBUTE.PRICE) || (retain ? null : AMOUNT.ZERO),
      quantity: retain?.get(ATTRIBUTE.QUANTITY)
        ? null
        : combination.get(ATTRIBUTE.QUANTITY) || (retain ? null : AMOUNT.ZERO),
      sku: retain?.get(ATTRIBUTE.SKU)
        ? null
        : combination.get(ATTRIBUTE.SKU) || null,
    };

    if (toImmutable) {
      return result.push(Map(variation));
    }

    result.push(variation);

    return result;
  }

  const value = {
    options: product.get(FORM.VARIATIONS).get(VARIANTS).reduce(reduceVariants, toImmutable ? EMPTY.LIST : []),
    variants: product.get(FORM.VARIATIONS).get(COMBINATIONS).reduce(reduceCombinations, toImmutable ? EMPTY.LIST : []),
  };

  if (retain) {
    value.retain = true;
  }

  if (toImmutable) {
    payload.value = Map(value);
  } else {
    payload.value = value;
  }

  return toImmutable ? Map(payload) : payload;
}

function shapeEtsyVariationsForAPI({ product, toImmutable }) {
  const payload = {};
  const retain = product.getIn([FORM.VARIATIONS, RETAIN]);
  const influence = product.getIn([FORM.VARIATIONS, INFLUENCE]);

  function reduceVariants(result, variantIndex) {
    const variation = {
      first: variantIndex === 0,
      id: -1 - variantIndex,
      options: toImmutable ? EMPTY.LIST : [],
    };

    const variant = product.getIn([FORM.VARIATIONS, VARIANTS, variantIndex]);

    if (!variant) {
      if (toImmutable) {
        return result.push(Map(variation));
      }

      result.push(variation);
      return result;
    }

    function reduceOptions(format) {
      return function reduce(options, item) {
        const optionId = item.get('id');
        const value = item.get('value');
        const valueId = item.get('valueId');
        const imageHash = retain
          ? null
          : item.get('imageHash', null);

        const label = format(value);
        const option = {
          label,
          value,
          valueId,
          imageHash,
          id: optionId,
          isAvailable: null,
        };

        if (toImmutable) {
          return options.push(Map(option));
        }

        options.push(option);
        return options;
      };
    }

    if (variant.get('id')) {
      variation.id = variant.get('id');
    }

    variation.propertyId = variant.get('property');
    variation.formattedName = variant.get('name');
    variation.influencesPrice = retain?.get(ATTRIBUTE.PRICE)
      ? false
      : influence.getIn([ATTRIBUTE.PRICE, variantIndex], false);

    variation.influencesQuantity = retain?.get(ATTRIBUTE.QUANTITY)
      ? false
      : influence.getIn([ATTRIBUTE.QUANTITY, variantIndex], false);

    variation.influencesReadinessState = retain?.get(ATTRIBUTE.PROCESSING_PROFILE)
      ? false
      : influence.getIn([ATTRIBUTE.PROCESSING_PROFILE, variantIndex], false);

    variation.influencesSku = retain?.get(ATTRIBUTE.SKU)
      ? false
      : influence.getIn([ATTRIBUTE.SKU, variantIndex], false);

    variation.scalingOptionId = variant.get('scale') || null;
    variation.isCustomProperty = variation.propertyId === CUSTOM_PROPERTY_IDS[ETSY][variantIndex];
    variation.options = variant
      .get('options')
      .reduce(
        reduceOptions(getOptionFormatter({ channel: ETSY, scale: variation.scalingOptionId })),
        variation.options
      );

    if (toImmutable) {
      return result.push(Map(variation));
    }

    result.push(variation);

    return result;
  }

  function reduceCombinations(result, combination, combinationIndex) {
    function reduceOptions(options, item) {
      const optionId = item.get('optionId');
      const variationId = item.get('variantId');
      const option = { optionId, variationId };

      if (toImmutable) {
        return options.push(Map(option));
      }

      options.push(option);
      return options;
    }

    const offering = {
      id: combination.get('id') || -1 - combinationIndex,
      visibility: combination.get(ATTRIBUTE.VISIBILITY),
      price: retain && (retain.get(ATTRIBUTE.PRICE) || getSize(influence.get(ATTRIBUTE.PRICE)) === 0)
        ? null
        : combination.get(ATTRIBUTE.PRICE),
      quantity: retain && (retain.get(ATTRIBUTE.QUANTITY) || getSize(influence.get(ATTRIBUTE.QUANTITY)) === 0)
        ? null
        : combination.get(ATTRIBUTE.QUANTITY),
      sku: retain && (retain.get(ATTRIBUTE.SKU) || getSize(influence.get(ATTRIBUTE.SKU)) === 0)
        ? null
        : combination.get(ATTRIBUTE.SKU),
      readiness_state_id: (
        retain && (
          retain.get(ATTRIBUTE.PROCESSING_PROFILE) ||
          getSize(influence.get(ATTRIBUTE.PROCESSING_PROFILE)) === 0
        )
      )
        ? null
        : combination.get(ATTRIBUTE.PROCESSING_PROFILE),
      variationOptions: combination.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []),
    };

    if (toImmutable) {
      return result.push(Map(offering));
    }

    result.push(offering);

    return result;
  }

  const value = {
    taxonomyId: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]),
    variations: CHANNEL_VARIANTS[ETSY].reduce(reduceVariants, toImmutable ? EMPTY.LIST : []),
    offerings: product.getIn([FORM.VARIATIONS, COMBINATIONS]).reduce(reduceCombinations, toImmutable ? EMPTY.LIST : []),
  };

  if (retain) {
    value.templateid = '-1';
  }

  if (toImmutable) {
    payload.value = Map(value);
  } else {
    payload.value = value;
  }

  return toImmutable ? Map(payload) : payload;
}

function shapeFaireVariationsForAPI({ product, toImmutable }) {
  const payload = {};
  const retain = product.getIn([FORM.VARIATIONS, RETAIN]);
  const influence = product.getIn([FORM.VARIATIONS, INFLUENCE]);

  function reduceVariants(result, variant) {
    function reduceOptions(values, item) {
      const value = item.get('value');
      const option = { value };

      if (toImmutable) {
        return values.push(Map(option));
      }

      values.push(option);
      return values;
    }

    const option = {
      name: variant.get('name'),
      value: variant.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []),
    };

    if (toImmutable) {
      return result.push(Map(option));
    }

    result.push(option);

    return result;
  }

  function reduceCombinations(result, combination) {
    function reduceOptions(options, item, index) {
      const value = item.get('name');
      const name = product.getIn([FORM.VARIATIONS, VARIANTS, index, 'name']);
      const option = { name, value };

      if (toImmutable) {
        return options.push(Map(option));
      }

      options.push(option);
      return options;
    }

    const variation = {
      available_quantity: retain?.get(ATTRIBUTE.QUANTITY)
        ? null
        : combination.get(ATTRIBUTE.QUANTITY) || null,
      backordered_until: (
        !retain?.get(ATTRIBUTE.QUANTITY) &&
        combination.get(ATTRIBUTE.QUANTITY) === AMOUNT.ZERO &&
        combination.get(ATTRIBUTE.RESTOCK_DATE) instanceof Date
          ? combination.get(ATTRIBUTE.RESTOCK_DATE).toISOString()
          : null
      ),
      gtin: retain?.get(ATTRIBUTE.GTIN)
        ? null
        : combination.get(ATTRIBUTE.GTIN) || null,
      image_url: retain
        ? null
        : combination.get('imageUrl'),
      measurements_distance_unit: shapeFaireDistanceUnitForApi(
        retain?.get(ATTRIBUTE.SHIPPING)
          ? null
          : combination.get(ATTRIBUTE.DISTANCE_UNIT)
      ),
      measurements_length: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.LENGTH) || null,
      measurements_width: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.WIDTH) || null,
      measurements_height: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.HEIGHT) || null,
      measurements_mass_unit: shapeFaireMassUnitForApi(
        retain?.get(ATTRIBUTE.SHIPPING)
          ? null
          : combination.get(ATTRIBUTE.MASS_UNIT)
      ),
      measurements_weight: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.WEIGHT) || null,
      options: combination.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []),
      orderability_type: (
        !retain?.get(ATTRIBUTE.PREORDER) && combination.get(ATTRIBUTE.PREORDER)
          ? 'PREORDER'
          : 'IMMEDIATE'
      ),
      preorder_details_expected_ship_date: (
        !retain?.get(ATTRIBUTE.PREORDER) &&
        combination.get(ATTRIBUTE.PREORDER) &&
        combination.get(ATTRIBUTE.START_DATE) instanceof Date
          ? getDateKey(combination.get(ATTRIBUTE.START_DATE))
          : null
      ),
      preorder_details_expected_ship_window_end_date: (
        !retain?.get(ATTRIBUTE.PREORDER) &&
        combination.get(ATTRIBUTE.PREORDER) &&
        combination.get(ATTRIBUTE.END_DATE) instanceof Date
          ? getDateKey(combination.get(ATTRIBUTE.END_DATE))
          : null
      ),
      preorder_details_stop_selling_at: (
        !retain?.get(ATTRIBUTE.PREORDER) &&
        combination.get(ATTRIBUTE.PREORDER) &&
        !influence.get(ATTRIBUTE.KEEP_ACTIVE) &&
        combination.get(ATTRIBUTE.DEADLINE_DATE) instanceof Date
          ? getDateKey(combination.get(ATTRIBUTE.DEADLINE_DATE))
          : null
      ),
      prices_retail_aud: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.AUD]) || null,
      prices_retail_cad: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.CAD]) || null,
      prices_retail_eur: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.EUR]) || null,
      prices_retail_gbp: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.GBP]) || null,
      prices_retail_usd: retain?.get(ATTRIBUTE.PRICE)
        ? null
        : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.USD]) || null,
      prices_wholesale_aud: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.AUD]) || null,
      prices_wholesale_cad: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.CAD]) || null,
      prices_wholesale_eur: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.EUR]) || null,
      prices_wholesale_gbp: retain?.get(ATTRIBUTE.PRICE) || !influence.get(ATTRIBUTE.INTERNATIONAL_PRICING)
        ? null
        : combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.GBP]) || null,
      prices_wholesale_usd: retain?.get(ATTRIBUTE.PRICE)
        ? null
        : combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, CURRENCY.USD]) || null,
      sku: retain?.get(ATTRIBUTE.SKU)
        ? null
        : combination.get(ATTRIBUTE.SKU) || null,
      tariff_code: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.TARIFF_CODE) || null,
    };

    if (toImmutable) {
      return result.push(Map(variation));
    }

    result.push(variation);

    return result;
  }

  const value = {
    options: product.getIn([FORM.VARIATIONS, VARIANTS]).reduce(reduceVariants, toImmutable ? EMPTY.LIST : []),
    variants: product.getIn([FORM.VARIATIONS, COMBINATIONS]).reduce(reduceCombinations, toImmutable ? EMPTY.LIST : []),
  };

  if (retain) {
    value.retain = true;
  }

  if (toImmutable) {
    payload.value = Map(value);
  } else {
    payload.value = value;
  }

  return toImmutable ? Map(payload) : payload;
}

function shapeShopifyVariationsForAPI({ product, toImmutable }) {
  const payload = {};
  const retain = product.getIn([FORM.VARIATIONS, RETAIN]);
  const deleted = product.getIn([FORM.VARIATIONS, DELETED], EMPTY.MAP);
  const variants = product.getIn([FORM.VARIATIONS, VARIANTS]);

  function reduceVariants(result, variantIndex) {
    const option = {
      shopify_name: '',
      shopify_position: String(variantIndex + 1),
      shopify_values: toImmutable ? EMPTY.LIST : [],
    };

    const variant = variants.get(variantIndex);

    if (!variant) {
      if (toImmutable) {
        return result.push(Map(option));
      }

      result.push(option);

      return result;
    }

    function reduceOptions(options, item) {
      if (isOptionDeleted({ deleted, option: item, variants, variantIndex })) return options;

      const value = item.get('value');

      if (toImmutable) {
        return options.push(value);
      }

      options.push(value);

      return options;
    }

    option.shopify_name = variant.get('name');
    option.shopify_position = variantIndex + 1;
    option.shopify_values = variant.get('options').reduce(reduceOptions, toImmutable ? EMPTY.LIST : []);

    if (retain) {
      option.id = variant.get('id') || '';
      option.shopify_template_id = -1;
    }

    if (toImmutable) {
      return result.push(Map(option));
    }

    result.push(option);

    return result;
  }

  function reduceCombinations(result, combination, combinationIndex) {
    if (isCombinationDeleted({ combination, deleted })) return result;

    const option1 = combination.getIn(['options', 0, 'name'], null);
    const option2 = combination.getIn(['options', 1, 'name'], null);
    const option3 = combination.getIn(['options', 2, 'name'], null);
    const physical = !retain?.get(ATTRIBUTE.SHIPPING) && combination.get(ATTRIBUTE.PHYSICAL);

    const variant = {
      deleted: false,
      id: (retain && combination.get('id')) || '',
      shopify_admin_graphql_api_id: null,
      shopify_created_at: null,
      shopify_updated_at: null,
      shopify_fulfillment_service: null,
      shopify_grams: null,
      shopify_inventory_item_id: null,
      shopify_old_inventory_quantity: null,
      shopify_option1: option1,
      shopify_option2: option2,
      shopify_option3: option3,
      shopify_position: combinationIndex + 1,
      shopify_cost: retain?.get(ATTRIBUTE.PRICE)
        ? null
        : combination.get(ATTRIBUTE.CPI) || (retain ? null : AMOUNT.ZERO),
      shopify_sku: retain?.get(ATTRIBUTE.SKU)
        ? null
        : combination.get(ATTRIBUTE.SKU) || null,
      shopify_price: retain?.get(ATTRIBUTE.PRICE)
        ? null
        : combination.get(ATTRIBUTE.PRICE) || (retain ? null : AMOUNT.ZERO),
      shopify_weight: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : (physical && combination.get(ATTRIBUTE.WEIGHT)) || (retain ? null : AMOUNT.ZERO),
      shopify_image_id: retain
        ? null
        : combination.get('imageId', null),
      shopify_barcode: retain?.get(ATTRIBUTE.BARCODE)
        ? null
        : combination.get(ATTRIBUTE.BARCODE) || null,
      shopify_weight_unit: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.UNIT) || (retain ? null : ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT]),
      shopify_image_hash: retain
        ? null
        : combination.get('imageHash', null),
      shopify_taxable: retain?.get(ATTRIBUTE.PRICE)
        ? true
        : combination.get(ATTRIBUTE.CHARGE_TAX) || false,
      shopify_compare_at_price: retain?.get(ATTRIBUTE.PRICE)
        ? null
        : combination.get(ATTRIBUTE.CAP) || (retain ? null : AMOUNT.ZERO),
      shopify_inventory_quantity: retain?.get(ATTRIBUTE.QUANTITY)
        ? null
        : combination.get(ATTRIBUTE.QUANTITY) || (retain ? null : AMOUNT.ZERO),
      shopify_requires_shipping: physical || false,
      shopify_harmonized_system_code: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.HS_CODE) || null,
      shopify_country_code_of_origin: retain?.get(ATTRIBUTE.SHIPPING)
        ? null
        : combination.get(ATTRIBUTE.COUNTRY_CODE) || null,
      shopify_inventory_management: retain?.get(ATTRIBUTE.QUANTITY)
        ? null
        : combination.get(ATTRIBUTE.TRACK_QUANTITY) ? SHOPIFY : null,
      shopify_inventory_policy: retain?.get(ATTRIBUTE.SHIPPING)
        ? 'deny'
        : combination.get(ATTRIBUTE.CONTINUE_SELLING) ? 'continue' : 'deny',
    };

    if (retain) {
      variant.shopify_template_id = '-1';
      variant.shopify_title = [option1, option2, option3].filter(isTruthy).join(SEPARATOR.SLASH_SPACE);
    } else {
      variant.shopify_id = 0;
    }

    if (toImmutable) {
      return result.push(Map(variant));
    }

    result.push(variant);

    return result;
  }

  if (isVariantDeleted({ deleted, variants })) {
    const empty = toImmutable ? EMPTY.LIST : [];
    const value = {
      options: toImmutable
        ? List([Map({ id: -1, options: empty }), Map({ id: -2, options: empty }), Map({ id: -3, options: empty })])
        : [{ id: -1, options: empty }, { id: -2, options: empty }, { id: -3, options: empty }],
      variants: empty,
    };

    if (toImmutable) {
      payload.value = Map(value);
    } else {
      payload.value = value;
    }
  } else {
    const value = {
      options: CHANNEL_VARIANTS[SHOPIFY].reduce(reduceVariants, toImmutable ? EMPTY.LIST : []),
      variants: product
        .getIn([FORM.VARIATIONS, COMBINATIONS])
        .reduce(reduceCombinations, toImmutable ? EMPTY.LIST : []),
    };

    if (toImmutable) {
      payload.value = Map(value);
    } else {
      payload.value = value;
    }
  }

  if (!retain) {
    const shopifyMeta = {
      individualSKU: true,
      individualPrice: true,
      individualBarcode: true,
      individualShipping: true,
    };

    if (toImmutable) {
      payload.shopifyMeta = Map(shopifyMeta);
    } else {
      payload.shopifyMeta = shopifyMeta;
    }
  } else {
    payload.individualSKU = true;
    payload.individualPrice = true;
    payload.individualBarcode = true;
    payload.individualShipping = true;

    const templateId = -1;
    payload.templateId = templateId;

    if (toImmutable) {
      payload.shopifyMeta = Map({ templateId });
    } else {
      payload.shopifyMeta = { templateId };
    }
  }

  return toImmutable ? Map(payload) : payload;
}

export function shapeVariationsForAPI({ product, toImmutable }) {
  switch (product.get('channel')) {
    case EBAY: {
      return shapeEbayVariationsForAPI({ product, toImmutable });
    }

    case ETSY: {
      return shapeEtsyVariationsForAPI({ product, toImmutable });
    }

    case FAIRE: {
      return shapeFaireVariationsForAPI({ product, toImmutable });
    }

    case SHOPIFY: {
      return shapeShopifyVariationsForAPI({ product, toImmutable });
    }

    default: {
      return undefined;
    }
  }
}
